import type { ComponentProps } from 'react'
import { useCallback, useMemo } from 'react'

import TextButton from 'LuxKit/Button/TextButton'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppSelector } from 'hooks/reduxHooks'
import offerPageURL from 'lib/offer/offerPageURL'
import { getCurrentUserId } from 'selectors/accountSelectors'
import useTripPlannerCTALabels from 'tripPlanner/hooks/useTripPlannerCTALabels'
import useOffer from 'hooks/Offers/useOffer'
import type { TripItem } from 'tripPlanner/types/tripItem'
import {
  buildMyEscapesOrderLink,
  isLeBooking,
} from 'tripPlanner/utils'
import { addTripIdParam } from 'tripPlanner/utils/url'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'

interface Props {
  item: TripItem
  size?: ComponentProps<typeof TextButton>['size']
}

export default function BookedOfferDetailsCTA({
  item,
  size = 'medium',
}: Props) {
  const { tripId } = item
  const canEdit = useCanEditTrip()
  const { bookingDetailsCopy, viewOfferCopy } = useTripPlannerCTALabels()
  const currentUserId = useAppSelector(getCurrentUserId)
  const isItemOwner = currentUserId === item.createdBy
  const [offer] = useOffer('code' in item ? item.code : undefined)
  const modalElement = useModalElementContext()

  const offerLink = useMemo(() => {
    if (!offer) return undefined
    return offerPageURL(offer)
  }, [offer])

  const myEscapesLink =
    canEdit ? buildMyEscapesOrderLink(item) : undefined
  const viewBooking = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  if (!isLeBooking(item)) return null

  if (isItemOwner && myEscapesLink) {
    return (
      <TextButton
        kind="secondary"
        size={size}
        variant="default"
        to={myEscapesLink}
        target="_blank"
        onClick={viewBooking}
      >
        {bookingDetailsCopy}
      </TextButton>
    )
  } else if (offerLink) {
    return (
      <TextButton
        kind="secondary"
        size={size}
        variant="default"
        to={addTripIdParam(offerLink, tripId, canEdit)}
        target="_blank"
      >
        {viewOfferCopy}
      </TextButton>
    )
  } else {
    return null
  }
}
